import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { clsx } from 'clsx';
import { useGetAiChatMessagesQuery } from 'store/slices/aiChats/apis/aiChatsApi';
import toAiChatContext from 'utils/toAiChatContext';

import AiChatComposer from './components/AiChatComposer';
import AiChatConversation from './components/AiChatConversation';
import toUiMessage from './utils/toUiMessage';

import type { ReactElement } from 'react';
import type IAiChatContext from 'store/slices/aiChats/interfaces/IAiChatContext';
import type IAiChat from './interfaces/IAiChat';

import styles from './sass/AiChat.module.scss';

/**
 * Newest page only. The API caps a page at 20 and there is no scroll-back paging yet.
 * Mirrors `Max(20)` on the backend `GetMessagesDto`. Change both together.
 * @see [12.8 Change a limit](../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
const MESSAGES_LIMIT = 20;

/** The composer has nothing to send into while the rows load. */
const IGNORE_SEND = (): void => undefined;

/**
 * A chat with the assistant: the messages of one chat, and a box to write the next one.
 *
 * Self-contained apart from the chat it reads — the caller passes a chat id, or leaves it out to
 * let the first message create a chat — so the same component can sit on a page of its own or
 * inside another screen. This component reads the stored rows and hands them to the conversation
 * once, on mount. The conversation then owns the messages, the ones it sends and the replies alike.
 */
function AiChat({
  chatId, conversationKey = 0, onChatCreated, screen, sendRef, className,
}: IAiChat): ReactElement {
  const { t } = useTranslation();
  // What the API is told about where the user is: the whole screen the caller handed over. Left
  // out of the body when the caller handed over nothing, rather than sent empty.
  const chatContext = useMemo<IAiChatContext | undefined>(
    () => (screen ? toAiChatContext(screen) : undefined),
    [screen],
  );
  // The chat the conversation seeds from: the one the caller was on when the conversation started.
  // Adopting a created chat changes `chatId` but not the key, so the seed — and the conversation
  // with it — stays put. Derived during the render, so the seed is right on the same pass.
  const [seed, setSeed] = useState({ key: conversationKey, chatId });

  if (seed.key !== conversationKey) {
    setSeed({ key: conversationKey, chatId });
  }

  const { data: rows, isLoading, error: loadError } = useGetAiChatMessagesQuery(
    seed.chatId ? { chatId: seed.chatId, limit: MESSAGES_LIMIT } : skipToken,
  );
  // The API answers newest-first; the composer sits at the bottom, so flip it to read top-down.
  const initialMessages = useMemo(() => [...(rows || [])].reverse().map(toUiMessage), [rows]);

  return (
    <div className={clsx(styles.root, className)}>
      {isLoading ? (
        <>
          <div className={styles.messages}>
            <p className={styles.note}>{t('aiChat.loading')}</p>
          </div>
          <AiChatComposer onSend={IGNORE_SEND} isDisabled screen={screen} />
        </>
      ) : (
        <AiChatConversation
          key={conversationKey}
          chatId={chatId}
          initialMessages={initialMessages}
          chatContext={chatContext}
          screen={screen}
          hasLoadError={!!loadError && !rows}
          onChatCreated={onChatCreated}
          sendRef={sendRef}
        />
      )}
    </div>
  );
}

export default AiChat;
